import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../../constants/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';

const SP_FONT = "'abcNormal','Inter','Arial',sans-serif";
const SP_FONT_FACE = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
`;

// Same visual language already approved for IntegrationsPanel/TeamsPanel/Hub
// (Apple-style cards, pill buttons, underline tabs) — reused here so
// Configuración feels like the same product as the rest of the dashboard,
// instead of the older plain-Tailwind/blue/sharp-corner look this replaces.
const SP_CSS = `
  .sp-btn{border:none;border-radius:980px;padding:10px 22px;font-size:13.5px;font-weight:500;cursor:pointer;letter-spacing:-0.01em;transition:opacity .15s ease,background .15s ease;font-family:'Inter',system-ui,sans-serif;white-space:nowrap;display:inline-flex;align-items:center;justify-content:center;gap:6px;}
  .sp-btn-dark{background:#0A0A0A;color:#fff;}
  .sp-btn-dark:hover:not(:disabled){opacity:.86;}
  .sp-btn-ghost{background:rgba(0,0,0,0.045);color:#0A0A0A;}
  .sp-btn-ghost:hover:not(:disabled){background:rgba(0,0,0,0.075);}
  .sp-btn-danger{background:#DC2626;color:#fff;}
  .sp-btn-danger:hover:not(:disabled){opacity:.88;}
  .sp-btn:disabled{opacity:.45;cursor:not-allowed;}
  .sp-link{background:none;border:none;padding:0;font-family:'Inter',system-ui,sans-serif;font-size:13.5px;font-weight:500;color:#0A0A0A;cursor:pointer;}
  .sp-link:hover:not(:disabled){text-decoration:underline;}
  .sp-link:disabled{opacity:.5;cursor:not-allowed;}
  .sp-link-muted{color:#6B7280;}
  .sp-link-danger{color:#B91C1C;}

  .sp-tabsbar{display:flex;flex-wrap:wrap;gap:22px;border-bottom:1px solid rgba(0,0,0,0.08);margin-bottom:24px;}
  .sp-tab{background:none;border:none;cursor:pointer;padding:10px 2px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:500;color:#6B7280;border-bottom:2px solid transparent;margin-bottom:-1px;transition:color .15s ease,border-color .15s ease;}
  .sp-tab:hover{color:#0A0A0A;}
  .sp-tab-active{color:#0A0A0A;border-color:#0A0A0A;}
  .sp-tab-danger{color:#B91C1C;}
  .sp-tab-danger:hover{color:#B91C1C;}
  .sp-tab-danger.sp-tab-active{color:#B91C1C;border-color:#B91C1C;}

  .sp-card{background:#fff;border:1px solid rgba(0,0,0,0.07);border-radius:16px;padding:22px 24px;margin-bottom:16px;}
  .sp-card-title{font-size:15px;font-weight:600;color:#0A0A0A;letter-spacing:-0.01em;margin:0;font-family:'Inter',system-ui,sans-serif;}
  .sp-card-sub{font-size:13px;color:#6B7280;margin:3px 0 0;}
  .sp-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:13px 16px;border-radius:12px;background:#FAFAFA;border:1px solid rgba(0,0,0,0.06);}
  .sp-subrow{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 16px;border-radius:10px;border:1px solid rgba(0,0,0,0.06);font-size:12px;color:#6B7280;margin-bottom:12px;}
  .sp-confirm-box{padding:16px;border-radius:12px;background:#FAFAFA;border:1px solid rgba(0,0,0,0.06);}
  .sp-support-actions{display:flex;flex-direction:column;gap:10px;}
  @media (min-width:640px){ .sp-support-actions{flex-direction:row;} }

  .sp-input{width:100%;box-sizing:border-box;padding:11px 14px;border-radius:10px;border:1px solid rgba(0,0,0,0.12);font-size:13.5px;color:#0A0A0A;font-family:'Inter',system-ui,sans-serif;outline:none;transition:border-color .15s,box-shadow .15s;background:#fff;}
  .sp-input:focus{border-color:#0A0A0A;box-shadow:0 0 0 3px rgba(10,10,10,0.08);}
  .sp-input-danger:focus{border-color:#DC2626;box-shadow:0 0 0 3px rgba(220,38,38,0.12);}
  .sp-label{display:block;font-size:12.5px;font-weight:600;color:#404040;margin-bottom:6px;}
  .sp-helper{font-size:12px;color:#9CA3AF;margin-top:6px;}
  .sp-error{font-size:12.5px;color:#DC2626;margin-top:2px;}
  .sp-banner-success{border-radius:12px;padding:12px 16px;font-size:13.5px;background:rgba(22,163,74,0.08);color:#15803D;border:1px solid rgba(22,163,74,0.18);margin-bottom:16px;}
  .sp-banner-error{border-radius:10px;padding:11px 14px;font-size:13px;background:rgba(220,38,38,0.06);color:#B91C1C;border:1px solid rgba(220,38,38,0.16);}

  .sp-avatar{width:56px;height:56px;border-radius:16px;background:#0A0A0A;display:flex;align-items:center;justify-content:center;color:#fff;font-size:20px;font-weight:600;flex-shrink:0;font-family:'Inter',system-ui,sans-serif;}
  .sp-lang-toggle{display:inline-flex;background:#F5F5F7;border-radius:10px;padding:3px;gap:2px;}
  .sp-lang-btn{border:none;background:none;border-radius:8px;padding:7px 20px;font-size:13px;font-weight:600;letter-spacing:0.01em;color:#9CA3AF;cursor:pointer;transition:background .15s,color .15s;font-family:'Inter',system-ui,sans-serif;}
  .sp-lang-btn-active{background:#fff;color:#0A0A0A;box-shadow:0 1px 3px rgba(0,0,0,0.1);}
  .sp-eye-btn{position:absolute;right:12px;top:50%;transform:translateY(-50%);background:none;border:none;color:#9CA3AF;cursor:pointer;display:flex;padding:2px;}
  .sp-eye-btn:hover{color:#6B7280;}

  .sp-progress-track{width:100%;height:6px;border-radius:999px;background:rgba(0,0,0,0.08);overflow:hidden;}
  .sp-progress-fill{height:100%;background:#0A0A0A;border-radius:999px;transition:width .3s ease;}

  .sp-danger-card{border:1px solid rgba(220,38,38,0.22);background:rgba(220,38,38,0.035);border-radius:16px;padding:22px 24px;}

  .sp-modal-overlay{position:fixed;inset:0;background:rgba(15,23,42,0.55);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:110;padding:16px;}
  .sp-modal{background:#fff;border-radius:20px;max-width:460px;width:100%;max-height:90vh;overflow-y:auto;box-shadow:0 24px 72px rgba(0,0,0,0.22);}

  @media (max-width:640px){
    .sp-card{padding:16px;border-radius:14px;}
    .sp-danger-card{padding:16px;border-radius:14px;}
    .sp-tabsbar{gap:14px;flex-wrap:nowrap;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;}
    .sp-tabsbar::-webkit-scrollbar{display:none;}
    .sp-tab{font-size:13px;flex-shrink:0;}
    .sp-avatar{width:48px;height:48px;border-radius:14px;font-size:17px;}
    .sp-modal{border-radius:16px;}
  }
`;

const EYE_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EYE_OFF_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const TABS = [
  { key: 'account', label: 'settings.tabs.account', fallback: 'Cuenta' },
  { key: 'plan', label: 'settings.tabs.plan', fallback: 'Plan y facturación' },
  { key: 'support', label: 'settings.tabs.support', fallback: 'Soporte' },
  { key: 'danger', label: 'settings.tabs.danger', fallback: 'Zona de peligro', danger: true },
];

const SettingsPanel = ({ user, onUserUpdate, setActiveTab }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { t, lang, setLang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [section, setSection] = useState('account');

  // Name editing state
  const [name, setName] = useState(user?.name || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMessage, setNameMessage] = useState({ type: '', text: '' });

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    if (user?.name) {
      setName(user.name);
    }
  }, [user]);

  // Handle name update
  const handleNameSave = async () => {
    if (!name.trim()) {
      setNameMessage({ type: 'error', text: t('settings.name.empty', 'El nombre no puede estar vacío') });
      return;
    }

    if (name.trim() === user.name) {
      setIsEditingName(false);
      return;
    }

    setNameSaving(true);
    setNameMessage({ type: '', text: '' });

    try {
      // Validate token before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      // Make the API call with explicit headers
      const response = await axios.put(
        `${API}/user/update-name`,
        { name: name.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          validateStatus: function (status) {
            // Accept any status code less than 500 as success
            return status < 500;
          }
        }
      );

      // Check if the response indicates success
      if (response.status === 200) {
        // Update local state and storage
        const updatedUser = { ...user, name: name.trim() };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        if (onUserUpdate) {
          onUserUpdate(updatedUser);
        }

        setNameMessage({ type: 'success', text: t('settings.name.saved', 'El nombre se ha modificado con exito.') });
        setIsEditingName(false);

        setTimeout(() => setNameMessage({ type: '', text: '' }), 5000);
      } else if (response.status === 401) {
        // Token expired or invalid
        setNameMessage({
          type: 'error',
          text: t('settings.sessionExpired', 'Tu sesión ha expirado. Por favor, vuelve a iniciar sesión.')
        });
        // Optionally redirect to login
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/login';
        }, 2000);
      } else {
        // Other errors
        setNameMessage({
          type: 'error',
          text: response.data?.detail || response.data?.message || t('settings.name.updateError', 'Error al actualizar el nombre')
        });
      }
    } catch (error) {
      console.error('Error updating name:', error);

      // If we have a response, check the status
      if (error.response) {
        if (error.response.status === 401) {
          setNameMessage({
            type: 'error',
            text: t('settings.sessionExpired', 'Tu sesión ha expirado. Por favor, vuelve a iniciar sesión.')
          });
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/login';
          }, 2000);
        } else if (error.response.status >= 200 && error.response.status < 300) {
          // Success response but axios threw error
          const updatedUser = { ...user, name: name.trim() };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          if (onUserUpdate) onUserUpdate(updatedUser);
          setNameMessage({ type: 'success', text: t('settings.name.saved', 'El nombre se ha modificado con exito.') });
          setIsEditingName(false);
        } else {
          setNameMessage({
            type: 'error',
            text: error.response.data?.detail || error.response.data?.message || t('settings.name.updateError', 'Error al actualizar el nombre')
          });
        }
      } else {
        // Network error or no response
        setNameMessage({
          type: 'error',
          text: t('settings.networkError', 'Error de conexión. Por favor, verifica tu internet e intenta de nuevo.')
        });
      }
    } finally {
      setNameSaving(false);
    }
  };

  // Password validation — matches the backend's validate_password_strength()
  // (also used at registration and password reset). This used to accept 6
  // characters with no case requirement, weaker than both of those, so a
  // password accepted here could pass but then be rejected if the same
  // person later went through the reset-password flow.
  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push(t('settings.password.minChars', 'Mínimo 8 caracteres'));
    if (!/[A-Z]/.test(password)) errors.push(t('settings.password.needUpper', 'Debe incluir una mayúscula'));
    if (!/[a-z]/.test(password)) errors.push(t('settings.password.needLower', 'Debe incluir una minúscula'));
    if (!/[0-9]/.test(password)) errors.push(t('settings.password.needNumber', 'Debe incluir números'));
    return errors;
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!currentPassword) {
      setPasswordMessage({ type: 'error', text: t('settings.password.enterCurrent', 'Ingresa tu contraseña actual') });
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setPasswordMessage({ type: 'error', text: passwordErrors.join('. ') });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: t('settings.password.mismatch', 'Las contraseñas no coinciden') });
      return;
    }

    setPasswordSaving(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      // Validate token before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await axios.put(
        `${API}/user/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          validateStatus: function (status) {
            // Accept any status code less than 500 as success
            return status < 500;
          }
        }
      );

      // Check if the response indicates success
      if (response.status === 200) {
        setPasswordMessage({ type: 'success', text: t('settings.password.saved', 'La contraseña se ha modificado con exito.') });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordForm(false);

        setTimeout(() => setPasswordMessage({ type: '', text: '' }), 5000);
      } else if (response.status === 401) {
        // Token expired or invalid
        setPasswordMessage({
          type: 'error',
          text: t('settings.sessionExpired', 'Tu sesión ha expirado. Por favor, vuelve a iniciar sesión.')
        });
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/login';
        }, 2000);
      } else {
        // Other errors
        setPasswordMessage({
          type: 'error',
          text: response.data?.detail || response.data?.message || t('settings.password.updateError', 'Error al cambiar la contraseña')
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);

      // If we have a response, check the status
      if (error.response) {
        if (error.response.status === 401) {
          setPasswordMessage({
            type: 'error',
            text: t('settings.sessionExpired', 'Tu sesión ha expirado. Por favor, vuelve a iniciar sesión.')
          });
          setTimeout(() => {
            localStorage.clear();
            window.location.href = '/login';
          }, 2000);
        } else if (error.response.status >= 200 && error.response.status < 300) {
          // Success response but axios threw error
          setPasswordMessage({ type: 'success', text: t('settings.password.saved', 'La contraseña se ha modificado con exito.') });
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setShowPasswordForm(false);
        } else {
          setPasswordMessage({
            type: 'error',
            text: error.response.data?.detail || error.response.data?.message || t('settings.password.updateError', 'Error al cambiar la contraseña')
          });
        }
      } else {
        // Network error or no response
        setPasswordMessage({
          type: 'error',
          text: t('settings.networkError', 'Error de conexión. Por favor, verifica tu internet e intenta de nuevo.')
        });
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const visibleTabs = TABS.filter((tab) => tab.key !== 'danger' || !user?.is_admin);
  const memberSinceDate = formatDateEs(user?.created_at, dateLocale);
  const memberSinceLabel = memberSinceDate
    ? t('settings.memberSince', `Miembro desde ${memberSinceDate}`).replace('{date}', memberSinceDate)
    : null;

  return (
    <div className="max-w-4xl">
      <style>{SP_FONT_FACE}</style>
      <style>{SP_CSS}</style>

      {/* Header */}
      <div className="mb-5 md:mb-6">
        <h2 style={{ fontSize: 28, fontWeight: 300, color: '#0A0A0A', letterSpacing: '-0.01em', fontFamily: SP_FONT }}>{t('settings.title', 'Configuración de Cuenta')}</h2>
        <p style={{ fontSize: 14, color: '#6B7280', marginTop: 4 }}>{t('settings.subtitle', 'Administra tu información personal y seguridad')}</p>
      </div>

      {/* Section tabs */}
      <div className="sp-tabsbar">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setSection(tab.key)}
            className={`sp-tab ${tab.danger ? 'sp-tab-danger' : ''} ${section === tab.key ? 'sp-tab-active' : ''}`}
          >
            {t(tab.label, tab.fallback)}
          </button>
        ))}
      </div>

      {section === 'account' && (
        <>
          {nameMessage.text && nameMessage.type === 'success' && (
            <div className="sp-banner-success">{nameMessage.text}</div>
          )}
          {passwordMessage.text && passwordMessage.type === 'success' && (
            <div className="sp-banner-success">{passwordMessage.text}</div>
          )}

          {/* Account Info Card */}
          <div className="sp-card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div className="sp-avatar">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: '#0A0A0A', margin: 0 }}>{user?.name || t('settings.user', 'Usuario')}</p>
              <p style={{ fontSize: 13, color: '#6B7280', margin: '2px 0 0' }}>{user?.email}</p>
              <p style={{ fontSize: 11.5, color: '#9CA3AF', margin: '4px 0 0' }}>
                {t('settings.email.locked', 'El correo electrónico no puede ser modificado')}
                {memberSinceLabel && ` · ${memberSinceLabel}`}
              </p>
            </div>
          </div>

          {/* Language Section */}
          <div className="sp-card">
            <div style={{ marginBottom: 16 }}>
              <h3 className="sp-card-title">{t('settings.language.title', 'Idioma')}</h3>
              <p className="sp-card-sub">{t('settings.language.sub', 'Elige el idioma de la plataforma')}</p>
            </div>
            <div className="sp-lang-toggle">
              <button type="button" onClick={() => setLang('es')} className={`sp-lang-btn ${lang === 'es' ? 'sp-lang-btn-active' : ''}`}>
                ES
              </button>
              <button type="button" onClick={() => setLang('en')} className={`sp-lang-btn ${lang === 'en' ? 'sp-lang-btn-active' : ''}`}>
                EN
              </button>
            </div>
          </div>

          {/* Name Section */}
          <div className="sp-card">
            <div style={{ marginBottom: 16 }}>
              <h3 className="sp-card-title">{t('settings.name.title', 'Nombre de Usuario')}</h3>
              <p className="sp-card-sub">{t('settings.name.sub', 'Este nombre se muestra en tu saludo y perfil')}</p>
            </div>

            {isEditingName ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label className="sp-label">{t('settings.name.new', 'Nuevo nombre')}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="sp-input"
                    placeholder={t('settings.name.placeholder', 'Ingresa tu nombre')}
                    autoFocus
                  />
                </div>

                {nameMessage.text && nameMessage.type === 'error' && (
                  <p className="sp-error">{nameMessage.text}</p>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleNameSave} disabled={nameSaving} className="sp-btn sp-btn-dark">
                    {nameSaving ? t('settings.saving', 'Guardando...') : t('settings.save', 'Guardar')}
                  </button>
                  <button
                    onClick={() => {
                      setName(user?.name || '');
                      setIsEditingName(false);
                      setNameMessage({ type: '', text: '' });
                    }}
                    className="sp-btn sp-btn-ghost"
                  >
                    {t('settings.cancel', 'Cancelar')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="sp-row">
                <span style={{ fontSize: 13.5, fontWeight: 500, color: '#0A0A0A' }}>{user?.name || t('settings.name.none', 'Sin nombre')}</span>
                <button onClick={() => setIsEditingName(true)} className="sp-link">
                  {t('settings.edit', 'Editar')}
                </button>
              </div>
            )}
          </div>

          {/* Password Section */}
          <div className="sp-card">
            <div style={{ marginBottom: 16 }}>
              <h3 className="sp-card-title">{t('settings.password.title', 'Contraseña')}</h3>
              <p className="sp-card-sub">{t('settings.password.sub', 'Actualiza tu contraseña de acceso')}</p>
            </div>

            {!showPasswordForm ? (
              <button onClick={() => setShowPasswordForm(true)} className="sp-btn sp-btn-ghost">
                {t('settings.password.change', 'Cambiar contraseña')}
              </button>
            ) : (
              <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Current Password */}
                <div>
                  <label className="sp-label">{t('settings.password.current', 'Contraseña actual')}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="sp-input"
                      style={{ paddingRight: 44 }}
                      placeholder={t('settings.password.currentPlaceholder', 'Ingresa tu contraseña actual')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                      className="sp-eye-btn"
                    >
                      {showPasswords.current ? EYE_OFF_ICON : EYE_ICON}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="sp-label">{t('settings.password.new', 'Nueva contraseña')}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="sp-input"
                      style={{ paddingRight: 44 }}
                      placeholder={t('settings.password.newPlaceholder', 'Ingresa tu nueva contraseña')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                      className="sp-eye-btn"
                    >
                      {showPasswords.new ? EYE_OFF_ICON : EYE_ICON}
                    </button>
                  </div>
                  <p className="sp-helper">{t('settings.password.requirements', 'Mínimo 8 caracteres, incluir mayúscula, minúscula y número')}</p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="sp-label">{t('settings.password.confirm', 'Confirmar nueva contraseña')}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="sp-input"
                      style={{ paddingRight: 44 }}
                      placeholder={t('settings.password.confirmPlaceholder', 'Confirma tu nueva contraseña')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                      className="sp-eye-btn"
                    >
                      {showPasswords.confirm ? EYE_OFF_ICON : EYE_ICON}
                    </button>
                  </div>
                </div>

                {passwordMessage.text && passwordMessage.type === 'error' && (
                  <p className="sp-error">{passwordMessage.text}</p>
                )}

                <div style={{ display: 'flex', gap: 10, paddingTop: 2 }}>
                  <button type="submit" disabled={passwordSaving} className="sp-btn sp-btn-dark">
                    {passwordSaving ? t('settings.saving', 'Guardando...') : t('settings.password.update', 'Actualizar contraseña')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordMessage({ type: '', text: '' });
                    }}
                    className="sp-btn sp-btn-ghost"
                  >
                    {t('settings.cancel', 'Cancelar')}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Integrations link — full interface now lives at its own tab, see IntegrationsPanel */}
          <div className="sp-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 0 }}>
            <div>
              <p style={{ fontSize: 13.5, fontWeight: 600, color: '#0A0A0A', margin: 0 }}>{t('settings.integrations.title', 'Integraciones')}</p>
              <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0' }}>{t('settings.integrations.sub', 'Google Sheets y próximas integraciones de CONTACT.')}</p>
            </div>
            <button
              type="button"
              data-testid="settings-manage-integrations-link"
              onClick={() => setActiveTab && setActiveTab('integrations')}
              className="sp-btn sp-btn-dark"
            >
              {t('settings.integrations.manage', 'Administrar integraciones')}
            </button>
          </div>
        </>
      )}

      {section === 'plan' && <MyPlanSection />}

      {section === 'support' && (
        <div className="sp-card" style={{ marginBottom: 0 }}>
          <div style={{ marginBottom: 16 }}>
            <h3 className="sp-card-title">{t('settings.support.title', 'Soporte')}</h3>
            <p className="sp-card-sub">{t('settings.support.sub', '¿Tienes dudas o necesitas ayuda? Contáctanos directamente.')}</p>
          </div>
          <div className="sp-support-actions">
            <a
              href="https://wa.me/56982036712?text=Hola%2C%20necesito%20ayuda%20con%20mi%20cuenta%20de%20CONTACT"
              target="_blank"
              rel="noopener noreferrer"
              className="sp-btn"
              style={{ background: '#25D366', color: '#fff', flex: 1 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.87 9.87 0 004.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0012.04 2zm5.8 14.02c-.24.68-1.4 1.3-1.94 1.38-.5.08-1.12.11-1.81-.11-.42-.13-.95-.31-1.64-.6-2.88-1.24-4.76-4.14-4.9-4.33-.14-.19-1.17-1.55-1.17-2.96s.73-2.1.99-2.39c.26-.28.56-.35.75-.35h.53c.17 0 .4-.06.62.48.24.58.81 2 .88 2.15.07.14.11.31.02.5-.09.19-.14.31-.28.48-.14.16-.29.36-.42.49-.14.14-.29.29-.12.57.16.28.72 1.19 1.55 1.93 1.06.95 1.96 1.24 2.24 1.38.28.14.44.12.6-.07.16-.19.68-.79.86-1.06.19-.28.37-.23.62-.14.26.09 1.62.77 1.9.9.28.14.46.21.53.32.07.12.07.65-.17 1.33z"/>
              </svg>
              {t('settings.support.talkToStaff', 'Hablar con personal')}
            </a>
            <a href="mailto:soporte@contactnetwork.cl" className="sp-btn sp-btn-ghost" style={{ flex: 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="5" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 7l9 6 9-6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('settings.support.sendEmail', 'Enviar correo')}
            </a>
          </div>
          <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 14 }}>soporte@contactnetwork.cl · +56 9 8203 6712</p>
        </div>
      )}

      {section === 'danger' && !user?.is_admin && (
        <DeleteAccountSection
          user={user}
          onDeleted={() => {
            logout();
            navigate('/', { replace: true });
          }}
        />
      )}
    </div>
  );
};

const BILLING_LABEL = { monthly: 'mensual', annual: 'anual' };
const billingLabel = (period, t) => t(`settings.billing.${period}`, BILLING_LABEL[period] || period);

const formatDateEs = (value, locale = 'es-CL') => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

const CANCEL_REASONS = [
  { value: '', label: 'Prefiero no decir' },
  { value: 'price', label: 'El precio' },
  { value: 'not_using', label: 'No lo uso lo suficiente' },
  { value: 'missing_feature', label: 'Falta una función que necesito' },
  { value: 'alternative', label: 'Encontré otra alternativa' },
  { value: 'other', label: 'Otro motivo' },
];
const cancelReasonLabel = (opt, t) => t(`settings.cancelReason.${opt.value || 'preferNotSay'}`, opt.label);

const MyPlanSection = () => {
  const { plan, openUpgrade, refreshPlan, profileLimit, profilesUsed } = usePlan();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmingPause, setConfirmingPause] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonDetail, setCancelReasonDetail] = useState('');
  const [showReasonDetailModal, setShowReasonDetailModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [switchingBilling, setSwitchingBilling] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadSubscription = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/me`);
      setSubscription(res.data);
    } catch (err) {
      setSubscription({ status: 'none' });
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/payments`);
      setPayments(res.data?.payments || []);
    } catch (err) {
      // Payment history is a nice-to-have — a failed fetch shouldn't
      // block the rest of Mi Plan from rendering.
    }
  };

  useEffect(() => {
    loadSubscription();
    loadPayments();
  }, []);

  const handleCancel = async () => {
    setCancelling(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await axios.post(`${API}/subscriptions/cancel`, {
        reason: cancelReason || null,
        reason_detail: cancelReason === 'other' ? (cancelReasonDetail || null) : null,
      });
      const accessUntil = res.data?.access_until;
      setSubscription((prev) => ({ ...prev, status: 'cancelled', access_until: accessUntil }));
      setConfirming(false);
      const untilLabel = formatDateEs(accessUntil, dateLocale);
      setMessage({
        type: 'success',
        text: untilLabel
          ? t('settings.plan.cancelledUntil', `Tu suscripción fue cancelada. Tu plan Pro seguirá activo hasta el ${untilLabel}.`).replace('{date}', untilLabel)
          : t('settings.plan.cancelledToFree', 'Tu suscripción fue cancelada. Volviste al plan Free.'),
      });
      await refreshPlan();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.detail || t('settings.plan.cancelError', 'No pudimos cancelar tu suscripción. Intenta de nuevo.'),
      });
    } finally {
      setCancelling(false);
    }
  };

  const handlePause = async () => {
    setPausing(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.post(`${API}/subscriptions/pause`);
      setConfirmingPause(false);
      setMessage({ type: 'success', text: t('settings.plan.paused', 'Tu suscripción quedó pausada. Puedes reanudarla cuando quieras.') });
      await loadSubscription();
      await refreshPlan();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.detail || t('settings.plan.pauseError', 'No pudimos pausar tu suscripción. Intenta de nuevo.'),
      });
    } finally {
      setPausing(false);
    }
  };

  const handleResume = async () => {
    setResuming(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.post(`${API}/subscriptions/resume`);
      setMessage({ type: 'success', text: t('settings.plan.resumed', 'Tu suscripción fue reanudada.') });
      await loadSubscription();
      await refreshPlan();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.detail || t('settings.plan.resumeError', 'No pudimos reanudar tu suscripción. Intenta de nuevo.'),
      });
    } finally {
      setResuming(false);
    }
  };

  const handleSwitchBilling = async (targetPeriod) => {
    setSwitchingBilling(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await axios.post(`${API}/subscriptions/switch-billing-period`, { billing_period: targetPeriod });
      if (res.data?.init_point) {
        window.location.href = res.data.init_point;
        return;
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.detail || t('settings.plan.billingSwitchError', 'No pudimos cambiar tu facturación. Intenta de nuevo.'),
      });
      setSwitchingBilling(false);
    }
  };

  const isActiveSubscription = subscription?.status === 'authorized';
  // Any "paused" subscription — whether the user paused it themselves (no
  // access_until) or MercadoPago auto-paused it after a failed renewal
  // (access_until set, grace period) — is resumable on the SAME
  // preapproval via /subscriptions/resume. Both cases get the same
  // "Reanudar" treatment instead of "Renovar", which would instead start
  // a brand new, separate subscription.
  const isResumable = subscription?.status === 'paused';
  const isEndingSubscription =
    subscription?.status === 'cancelled' &&
    subscription?.access_until &&
    new Date(subscription.access_until) > new Date();
  const nextChargeLabel = formatDateEs(subscription?.next_payment_date, dateLocale);
  const accessUntilLabel = formatDateEs(subscription?.access_until, dateLocale);
  const maxProfiles = profileLimit ?? plan?.limits?.max_profiles;
  const otherBillingPeriod = subscription?.billing_period === 'annual' ? 'monthly' : 'annual';

  return (
    <div className="sp-card" style={{ marginBottom: 0 }}>
      <div style={{ marginBottom: 16 }}>
        <h3 className="sp-card-title">{t('settings.plan.title', 'Mi Plan')}</h3>
        <p className="sp-card-sub">{t('settings.plan.sub', 'Revisa tu plan actual y administra tu suscripción')}</p>
      </div>

      <div className="sp-row" style={{ marginBottom: 12 }}>
        <div>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: '#0A0A0A' }}>{plan?.plan_name || 'Free'}</span>
          {plan?.plan_id !== 'free' && !isActiveSubscription && !isEndingSubscription && !isResumable && (
            <p style={{ fontSize: 11.5, color: '#9CA3AF', margin: '2px 0 0' }}>{t('settings.plan.activatedByTeam', 'Activado por el equipo de Contact Network')}</p>
          )}
          {isActiveSubscription && (
            <p style={{ fontSize: 11.5, color: '#9CA3AF', margin: '2px 0 0' }}>
              {t('settings.plan.subscription', 'Suscripción')} {billingLabel(subscription.billing_period, t)} · $
              {Number(subscription.amount_clp || 0).toLocaleString(dateLocale)} CLP
              {nextChargeLabel && ` · ${t('settings.plan.nextCharge', 'próximo cobro')} ${nextChargeLabel}`}
            </p>
          )}
          {isEndingSubscription && (
            <p style={{ fontSize: 11.5, color: '#9CA3AF', margin: '2px 0 0' }}>
              {t('settings.plan.proEndsOn', 'Tu plan Pro termina el')} {accessUntilLabel}
            </p>
          )}
          {isResumable && (
            <p style={{ fontSize: 11.5, color: '#9CA3AF', margin: '2px 0 0' }}>
              {accessUntilLabel ? t('settings.plan.pausedUntil', `Suscripción pausada · acceso hasta el ${accessUntilLabel}`).replace('{date}', accessUntilLabel) : t('settings.plan.pausedShort', 'Suscripción pausada')}
            </p>
          )}
        </div>
        {plan?.plan_id === 'free' && !isEndingSubscription && !isResumable && (
          <button onClick={() => openUpgrade()} className="sp-link" style={{ flexShrink: 0 }}>
            {t('settings.plan.viewPlans', 'Ver planes')}
          </button>
        )}
        {isResumable && (
          <button onClick={handleResume} disabled={resuming} className="sp-link" style={{ flexShrink: 0 }}>
            {resuming ? t('settings.plan.resuming', 'Reanudando…') : t('settings.plan.resumeSubscription', 'Reanudar suscripción')}
          </button>
        )}
        {isEndingSubscription && (
          <button onClick={() => openUpgrade()} className="sp-link" style={{ flexShrink: 0 }}>
            {t('settings.plan.renewPlan', 'Renovar mi plan')}
          </button>
        )}
      </div>

      {isActiveSubscription && (
        <div className="sp-subrow">
          <span>{t('settings.plan.billing', 'Facturación')} {billingLabel(subscription.billing_period, t)}</span>
          <button onClick={() => handleSwitchBilling(otherBillingPeriod)} disabled={switchingBilling} className="sp-link">
            {switchingBilling ? t('settings.plan.redirecting', 'Redirigiendo…') : t('settings.plan.switchTo', `Cambiar a ${BILLING_LABEL[otherBillingPeriod]}`).replace('{period}', billingLabel(otherBillingPeriod, t))}
          </button>
        </div>
      )}

      {maxProfiles != null && (
        <div className="sp-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#6B7280' }}>
            <span>{t('settings.plan.profilesUsed', 'Perfiles usados')}</span>
            <span>{profilesUsed ?? 0}/{maxProfiles}</span>
          </div>
          <div className="sp-progress-track">
            <div
              className="sp-progress-fill"
              style={{ width: `${Math.min(100, ((profilesUsed ?? 0) / Math.max(1, maxProfiles)) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {payments.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 11.5, fontWeight: 600, color: '#6B7280', marginBottom: 8 }}>{t('settings.plan.paymentHistory', 'Historial de pagos')}</p>
          <div className="rounded-xl border border-[rgba(0,0,0,0.07)] divide-y divide-gray-100 overflow-hidden">
            {payments.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-2 text-xs">
                <span className="text-gray-500">{formatDateEs(p.created_at, dateLocale)}</span>
                <span className="text-gray-900 font-medium">${Number(p.amount_clp || 0).toLocaleString(dateLocale)} CLP</span>
                <span
                  className={
                    p.status === 'approved' ? 'text-green-700' : p.status === 'rejected' ? 'text-red-600' : 'text-gray-500'
                  }
                >
                  {p.status === 'approved' ? t('settings.plan.paymentApproved', 'Aprobado') : p.status === 'rejected' ? t('settings.plan.paymentRejected', 'Rechazado') : p.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {message.text && (
        <div className={message.type === 'error' ? 'sp-banner-error' : 'sp-banner-success'} style={{ marginBottom: 12 }}>
          {message.text}
        </div>
      )}

      {!loading && isActiveSubscription && (
        confirming ? (
          <div className="sp-confirm-box">
            <p style={{ fontSize: 13, color: '#404040', marginBottom: 12 }}>
              {nextChargeLabel
                ? t('settings.plan.cancelConfirmWithDate', `No se realizarán más cobros. Tu plan Pro seguirá activo hasta el ${nextChargeLabel}, y después tu cuenta pasará automáticamente al plan Free. ¿Confirmas?`).replace('{date}', nextChargeLabel)
                : t('settings.plan.cancelConfirmNoDate', 'No se realizarán más cobros. Tu cuenta pasará al plan Free apenas se procese la cancelación. ¿Confirmas?')}
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 11.5, color: '#9CA3AF', marginBottom: 5 }}>{t('settings.plan.cancelReasonLabel', '¿Por qué cancelas? (opcional)')}</label>
              <select
                value={cancelReason}
                onChange={(e) => {
                  const val = e.target.value;
                  setCancelReason(val);
                  if (val === 'other') setShowReasonDetailModal(true);
                }}
                className="sp-input"
              >
                {CANCEL_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{cancelReasonLabel(r, t)}</option>
                ))}
              </select>
              {cancelReason === 'other' && cancelReasonDetail && (
                <p style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 6, fontStyle: 'italic' }}>"{cancelReasonDetail}"</p>
              )}
              {cancelReason === 'other' && (
                <button type="button" onClick={() => setShowReasonDetailModal(true)} className="sp-link" style={{ fontSize: 11.5, marginTop: 6 }}>
                  {cancelReasonDetail ? t('settings.plan.editComment', 'Editar comentario') : t('settings.plan.addComment', 'Agregar un comentario')}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleCancel} disabled={cancelling} className="sp-btn sp-btn-danger">
                {cancelling ? t('settings.plan.cancelling', 'Cancelando…') : t('settings.plan.yesCancel', 'Sí, cancelar')}
              </button>
              <button onClick={() => setConfirming(false)} disabled={cancelling} className="sp-btn sp-btn-ghost">
                {t('settings.plan.noKeep', 'No, mantener')}
              </button>
            </div>
          </div>
        ) : confirmingPause ? (
          <div className="sp-confirm-box">
            <p style={{ fontSize: 13, color: '#404040', marginBottom: 12 }}>
              {t('settings.plan.pauseConfirm', 'Tu plan pasará a Free de inmediato y no se realizarán cobros mientras esté pausada. Puedes reanudarla cuando quieras, sin volver a ingresar tu tarjeta. ¿Confirmas?')}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handlePause} disabled={pausing} className="sp-btn sp-btn-dark">
                {pausing ? t('settings.plan.pausing', 'Pausando…') : t('settings.plan.yesPause', 'Sí, pausar')}
              </button>
              <button onClick={() => setConfirmingPause(false)} disabled={pausing} className="sp-btn sp-btn-ghost">
                {t('settings.plan.noKeep', 'No, mantener')}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <button onClick={() => setConfirming(true)} className="sp-link sp-link-danger">
              {t('settings.plan.cancelSubscription', 'Cancelar suscripción')}
            </button>
            <button onClick={() => setConfirmingPause(true)} className="sp-link sp-link-muted">
              {t('settings.plan.pauseInsteadOfCancel', 'Pausar en vez de cancelar')}
            </button>
          </div>
        )
      )}

      {showReasonDetailModal && (
        <div className="sp-modal-overlay" onClick={() => setShowReasonDetailModal(false)}>
          <div className="sp-modal" style={{ maxWidth: 380, padding: 24 }} onClick={(e) => e.stopPropagation()}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#0A0A0A', marginBottom: 4 }}>{t('settings.plan.tellUsMore', 'Cuéntanos más')}</h4>
            <p style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 12 }}>{t('settings.plan.optionalHelpsUs', 'Opcional. Nos ayuda a mejorar.')}</p>
            <textarea
              autoFocus
              value={cancelReasonDetail}
              onChange={(e) => setCancelReasonDetail(e.target.value)}
              placeholder={t('settings.plan.commentPlaceholder', '¿Qué te gustaría que supiéramos?')}
              rows={4}
              className="sp-input"
              style={{ resize: 'none', marginBottom: 16 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowReasonDetailModal(false)} className="sp-link sp-link-muted">
                {t('settings.close', 'Cerrar')}
              </button>
              <button onClick={() => setShowReasonDetailModal(false)} className="sp-btn sp-btn-dark">
                {t('settings.save', 'Guardar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DeleteAccountSection = ({ user, onDeleted }) => {
  const { t } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const requiresPassword = true; // backend will tell us if not needed
  const confirmKeyword = t('settings.delete.keyword', 'ELIMINAR');

  const close = () => {
    if (submitting) return;
    setShowModal(false);
    setPassword('');
    setConfirmText('');
    setError('');
  };

  const submit = async () => {
    setError('');
    if (confirmText.trim().toUpperCase() !== confirmKeyword) {
      setError(t('settings.delete.typeToConfirm', `Escribe "${confirmKeyword}" para confirmar.`).replace('{keyword}', confirmKeyword));
      return;
    }
    setSubmitting(true);
    try {
      await axios.delete(`${API}/user/me`, {
        data: { password: password || null },
      });
      onDeleted();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (detail?.message || t('settings.delete.deleteError', 'No se pudo eliminar la cuenta.')));
      setSubmitting(false);
    }
  };

  return (
    <div className="sp-danger-card" data-testid="danger-zone">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2">
            <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-[#B91C1C]">{t('settings.delete.dangerZone', 'Zona peligrosa')}</h3>
          <p className="text-sm text-[#B91C1C] mt-1 leading-relaxed" style={{ opacity: 0.85 }}>
            {t('settings.delete.warningPrefix', 'Al eliminar tu cuenta se borrarán')} <strong>{t('settings.delete.permanently', 'permanentemente')}</strong> {t('settings.delete.warningSuffix', 'todos tus perfiles, leads, analíticas, contactos del Hub y configuración. Esta acción no se puede deshacer.')}
          </p>
          <button
            type="button"
            data-testid="open-delete-account-modal"
            onClick={() => setShowModal(true)}
            className="sp-btn sp-btn-danger"
            style={{ marginTop: 16 }}
          >
            {t('settings.delete.deleteMyAccount', 'Eliminar mi cuenta')}
          </button>
        </div>
      </div>

      {showModal && (
        <div data-testid="delete-account-modal" className="sp-modal-overlay" onClick={close}>
          <div className="sp-modal" data-testid="delete-account-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 md:px-6 pt-4 md:pt-6 pb-4 border-b border-gray-100">
              <h3 className="text-base md:text-lg font-semibold text-[#B91C1C]">{t('settings.delete.confirmTitle', '¿Eliminar tu cuenta?')}</h3>
              <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
                {t('settings.delete.confirmPrefix', 'Vamos a borrar todos tus datos de')} <strong style={{ color: '#0A0A0A' }}>{user?.email}</strong>. {t('settings.delete.irreversible', 'Esta acción es irreversible.')}
              </p>
            </div>

            <div className="p-4 md:p-6 space-y-4">
              <ul className="text-sm space-y-1.5 pl-4 list-disc" style={{ color: '#404040' }}>
                <li>{t('settings.delete.listProfiles', 'Todos tus perfiles públicos y sus URLs')}</li>
                <li>{t('settings.delete.listLeads', 'Tus leads, analíticas y contactos del Hub')}</li>
                <li>{t('settings.delete.listSubscription', 'Tu suscripción y datos de configuración')}</li>
              </ul>

              {requiresPassword && (
                <div>
                  <label className="sp-label" style={{ textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: 11 }}>
                    {t('settings.delete.confirmPassword', 'Confirma tu contraseña')}
                  </label>
                  <input
                    data-testid="delete-account-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('settings.password.currentPlaceholder2', 'Tu contraseña actual')}
                    autoComplete="current-password"
                    className="sp-input sp-input-danger"
                  />
                  <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>
                    {t('settings.delete.googleNoPassword', 'Si te registraste con Google sin contraseña, deja este campo vacío.')}
                  </p>
                </div>
              )}

              <div>
                <label className="sp-label" style={{ textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: 11 }}>
                  {t('settings.delete.typePrefix', 'Escribe')} <span className="font-mono text-[#B91C1C]">{confirmKeyword}</span> {t('settings.delete.typeSuffix', 'para confirmar')}
                </label>
                <input
                  data-testid="delete-account-confirm"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={confirmKeyword}
                  className="sp-input sp-input-danger font-mono"
                />
              </div>

              {error && (
                <div className="sp-banner-error" data-testid="delete-account-error">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  data-testid="delete-account-confirm-btn"
                  type="button"
                  onClick={submit}
                  disabled={submitting || confirmText.trim().toUpperCase() !== confirmKeyword}
                  className="sp-btn sp-btn-danger"
                  style={{ flex: 1 }}
                >
                  {submitting ? t('settings.delete.deleting', 'Eliminando…') : t('settings.delete.deletePermanently', 'Eliminar definitivamente')}
                </button>
                <button type="button" onClick={close} disabled={submitting} className="sp-btn sp-btn-ghost">
                  {t('settings.cancel', 'Cancelar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;
